import { ACCOUNT_STATUS, TypeIdSchema, account, ACCOUNT_INITIAL_SYNC_STATUS } from '@unipile/db';
import { Static, Type } from '@sinclair/typebox';
import { createInsertSchema, createSelectSchema } from 'drizzle-typebox';
import { ProviderSchema } from '../../common/provider.schema';
import { CustomProxySchema } from '../../common/proxys.schema';
import type { Proxy } from '../../common/proxys.schema';
import { StringEnum } from '../../utils/typebox';
import type { ApplicationSetting } from '../application/application-setting.schema';
import type { Application } from '../application/application.schema';
import type { Organization } from '../organization/organization.schema';

export const PUBLIC_ACCOUNT_STATUS = ['running', 'errored', 'disconnected', 'degraded', 'partial'] as const;

export const AccountStatusSchema = StringEnum([...PUBLIC_ACCOUNT_STATUS], {
  description: `The current status of the account.
    - \`running\` is Operational.
    - \`errored\` is Service Interuption.
    - \`disconnected\` requires authentication.
    - \`degraded\` means at least one product is running while another is errored; inspect \`metadata.products_connection_status\`.
    - \`partial\` means at least one product is running while another requires authentication; inspect \`metadata.products_connection_status\`.
    Learn more about [Status & Lifecycle](https://developer.unipile.com/v2.0/docs/status-lifecycle).`,
});

const AccountInitialSyncSchema = Type.Object(
  {
    status: StringEnum([...ACCOUNT_INITIAL_SYNC_STATUS], {
      description: `The state of an account’s initial provider sync.
  - \`pending\`sync hasn’t started yet.
  - \`running\` sync is currently in progress.
  - \`failed\` sync could not be completed due to an error.
  - \`completed\` sync finished successfully.
  .`,
    }),
    started_at: Type.Optional(Type.String({ description: 'The date and time the initial sync started.' })),
  },
  {
    description: `Sync details if the account has initial sync enabled.`,
  },
);

export const AccountSchema = createSelectSchema(account, {
  status: StringEnum([...ACCOUNT_STATUS]),
  initial_sync: Type.Optional(AccountInitialSyncSchema),
  provider: ProviderSchema,
});

export const InsertAccountSchema = createInsertSchema(account);

export const AccountMetadataSchema = Type.Object(
  {
    auto_proxy_country: Type.Optional(
      Type.String({
        description:
          'The preferred country for automatic proxy selection. A country inferred from an IP may be outside Unipile’s proxy offering. The current proxy may be in another country when fallback is allowed and no proxy is available in the preferred country.',
      }),
    ),
    allow_country_fallback: Type.Optional(
      Type.Boolean({
        description:
          'Whether an available proxy from another country may be used when none is available in the rotation country, during authentication and throughout the account lifetime. Defaults to true. A country included in Unipile’s proxy offering may still have no proxy currently available. If false, authentication fails when no proxy is available in the rotation country. If the selected proxy later fails and no replacement is available there, the account enters Service interruption. Rotation to another available proxy in that country remains allowed.',
      }),
    ),
    connection_method: Type.Optional(
      StringEnum(['credentials', 'cookies', 'unilogin'], {
        description: 'The authentication method that currently owns the account session lifecycle.',
      }),
    ),
    v1_account_id: Type.Optional(
      Type.String({ description: 'The ID of the account in the v1 if the account is migrated from v1.' }),
    ),
    v1_instance_port: Type.Optional(
      Type.Number({ description: 'The port of the v1 instance from which the account was migrated.' }),
    ),
    products_connection_status: Type.Optional(
      Type.Record(Type.String(), StringEnum(['running', 'disconnected', 'errored']), {
        description: 'The status of the connection to a product, if the account is connected to multiple products.',
      }),
    ),
    products_display_names: Type.Optional(
      Type.Record(Type.String(), Type.String(), {
        description:
          'The human-readable name of a product, keyed like products_connection_status, when the product has one (e.g. the name of a Facebook Page).',
      }),
    ),
    custom_data: Type.Optional(
      Type.Record(Type.String(), Type.String(), {
        description: 'Custom data of the account. This is where you can store custom data using "Update an account" method.',
      }),
    ),
  },
  { description: 'Metadata associated with the Account.' },
);

export const PatchAccountBodySchema = Type.Object(
  {
    account_scope_id: Type.Optional(
      TypeIdSchema('accountScope', {
        description:
          'The Scope to permanently assign to an unscoped Account. This field requires a Service API Key, and the Scope cannot be removed or replaced after assignment.',
      }),
    ),
    metadata: Type.Optional(
      Type.Record(Type.String(), Type.String(), {
        description:
          'Custom key-value data for the account. Replaces the account `metadata.custom_data` field; other metadata fields are not modified. Any fields not provided will be removed.',
      }),
    ),
    auto_proxy_country: Type.Optional(
      Type.String({
        description:
          'The rotation country to use for Automatic Proxy Protection. Choose from the <a href="https://developer.unipile.com/v2.0/docs/proxys#countries">countries where Unipile offers automatic proxies</a>. A proxy may be temporarily unavailable even in a country included in the offering.',
      }),
    ),
    allow_country_fallback: Type.Optional(
      Type.Boolean({
        description:
          'Whether Automatic Proxy Protection may use an available proxy from another country when none is available in the rotation country, during authentication and throughout the account lifetime. Defaults to true. An explicitly selected rotation country must be part of Unipile’s proxy offering for the provider, but a proxy may still be temporarily unavailable there. If false, authentication fails when no proxy is available in the rotation country; if the selected proxy later fails without an available replacement there, the account enters Service interruption.',
      }),
    ),
    proxy: Type.Optional(
      Type.Union([CustomProxySchema, Type.Null()], {
        description:
          'A new proxy configuration for the account. This will restart the account. Set to `null` to remove the proxy. Removing the proxy will revert the account to using Automatic Proxy Protection if required for the provider.',
      }),
    ),
  },
  {
    additionalProperties: false,
  },
);

export const PublicAccountSchema = Type.Object(
  {
    object: Type.Literal('Account', { description: 'The type of the returned object.' }),
    user_id: Type.String({ description: 'The ID of the user that owns the account for the provider.' }),
    name: Type.Optional(
      Type.String({
        description:
          'The name of the account. This is usually the display name or public identifier (phone number, email address, etc.) of the user that owns the account.',
      }),
    ),
    created_at: Type.String({ description: 'The date and time the account was linked with Unipile.' }),
    id: TypeIdSchema('account', { description: 'ID of the account.' }),
    application_id: TypeIdSchema('app', { description: 'ID of the parent application.' }),
    account_scope_id: Type.Optional(
      Type.Union([TypeIdSchema('accountScope'), Type.Null()], {
        description: 'ID of the Scope assigned to the Account. Once assigned, the Scope cannot be removed or replaced.',
      }),
    ),
    status: AccountStatusSchema,
    is_locked: Type.Boolean({ description: 'Whether API access to the account is locked.' }),
    provider: ProviderSchema,
    oauth_scope: Type.Optional(
      Type.String({
        description: 'If the provider is OAuth, this is the scope of comma-separated permissions granted to the account.',
      }),
    ),
    metadata: AccountMetadataSchema,
    initial_sync: Type.Optional(AccountInitialSyncSchema),
    proxy: Type.Optional(
      Type.Object(
        {
          country: Type.Union([Type.String(), Type.Null()], {
            description: 'The country of the proxy. If null, the proxy has no defined country.',
          }),
          auto_proxy: Type.Boolean({ description: 'Whether the proxy was set automatically by Unipile.' }),
          host: Type.Optional(Type.String({ description: 'The host of the custom proxy.' })),
          port: Type.Optional(Type.Number({ description: 'The port of the custom proxy.' })),
          username: Type.Optional(Type.String({ description: 'The username used to authenticate to the custom proxy.' })),
          protocol: Type.Optional(Type.String({ description: 'The protocol of the custom proxy.' })),
        },
        { description: 'The proxy used for the account.' },
      ),
    ),
  },
  { title: 'Account' },
);

export type Account = typeof account.$inferSelect;
export type AccountMetadata = Static<typeof AccountMetadataSchema>;
export type InsertAccount = typeof account.$inferInsert;
export type PatchAccountBody = Static<typeof PatchAccountBodySchema>;
export type PublicAccount = Static<typeof PublicAccountSchema>;

/** Account loaded with the application settings and organization required to start a realtime pool connection. */
export type RealtimePoolAccount = Account & {
  application: Application & { settings: ApplicationSetting[]; organization: Organization };
  proxy: Proxy | null;
};
